import { useParams } from 'react-router-dom'
import { useProcotol } from '@services/protocols/protocols.hook'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Stack,
  Alert
} from '@mui/material'
import { DeleteForever } from '@mui/icons-material'
import { usePersistedFormik } from '../hooks/usePersistedFormik'
import * as Yup from 'yup'
import { PROTOCOL_TYPE } from '@services/protocols/protocols.consts'
import { getProtocols } from '@services/protocols/protocols.helpers'
import AutoSaveIndicator, { AutoSaveStyles } from './AutoSaveIndicator'
import { useMemo } from 'react'
import FurnitureTypePreview from './FurnitureTypePreview'

import {
  useFlatFurnitures,
  useProtocolFurnitures,
  useResidentialFurnitureProducers,
  useResidentialFurnitureTypes,
  useWarehouseFurnitures
} from '@services/protocols/hooks/protocolFurnitures'

function ProtocolFurnitures({ isReadOnly }: { isReadOnly: boolean }) {
  const { id } = useParams<{ id: string }>()
  const { protocol } = useProcotol(Number(id))
  const { currentProtocol, oppositeProtocol } = getProtocols(protocol)

  const { createProtocolFurniture, deleteProtocolFurniture, protocolFurnitures } =
    useProtocolFurnitures(Number(currentProtocol?.id))
  const { flatFurnitures } = useFlatFurnitures(Number(currentProtocol?.id))
  const { warehouseFurnitures } = useWarehouseFurnitures(Number(currentProtocol?.id))

  const { protocolFurnitures: oppositeProtocolFurnitures } = useProtocolFurnitures(
    Number(oppositeProtocol?.id)
  )

  const { residentialFurnitureProducers, getFurnitureProducerNameById } =
    useResidentialFurnitureProducers()
  const { residentialFurnitureTypes, getFurnitureTypeNameById } = useResidentialFurnitureTypes()

  const furnitureCreateform = usePersistedFormik(
    {
      validateOnMount: false,
      validateOnChange: false,
      initialValues: {
        furniture: null
      },
      validationSchema: Yup.object().shape({
        furniture: Yup.number().required('Wybierz mebel')
      }),
      onSubmit: async (values: { furniture: number | null }) => {
        await createProtocolFurniture.mutateAsync({
          furniture: values.furniture as number
        })
        furnitureCreateform.resetForm()
      }
    },
    {
      formType: 'furniture',
      protocolId: Number(currentProtocol?.id || id),
      autoSaveInterval: 3000,
      showNotifications: false, // Less intrusive for furniture forms
      clearOnSubmit: true
    }
  )

  const removeProtocolFurniture = async (furnitureId: number) => {
    furnitureCreateform.setSubmitting(true)
    await deleteProtocolFurniture.mutateAsync(furnitureId)
    furnitureCreateform.setSubmitting(false)
  }

  const furnitureList = useMemo(() => {
    return [
      ...flatFurnitures.map((item) => ({
        key: 'flat-' + item.id,
        value: item.id,
        label: !!item.producer
          ? `${getFurnitureTypeNameById(item.furnitureType)} - ${getFurnitureProducerNameById(item.producer)} - ${item.model}`
          : `${getFurnitureTypeNameById(item.furnitureType)} - ${item.model}`
      })),
      ...warehouseFurnitures.map((item) => ({
        key: 'warehouse-' + item.id,
        value: item.id,
        label: !!item.producer
          ? `[Magazyn] ${getFurnitureTypeNameById(item.furnitureType)} - ${getFurnitureProducerNameById(item.producer)} - ${item.model}`
          : `[Magazyn] ${getFurnitureTypeNameById(item.furnitureType)} - ${item.model}`
      }))
    ]
  }, [
    flatFurnitures,
    warehouseFurnitures,
    residentialFurnitureTypes,
    residentialFurnitureProducers
  ])

  return (
    <Box>
      {/* Unsaved changes indicator */}
      {furnitureCreateform.hasUnsavedChanges && (
        <Alert
          severity='warning'
          sx={{ mb: 2 }}
          action={
            <Button size='small' onClick={furnitureCreateform.restoreFromSaved} sx={{ mr: 1 }}>
              Odrzuć zmiany
            </Button>
          }
        >
          Formularz zawiera niezapisane zmiany
        </Alert>
      )}

      {protocolFurnitures.length === 0 && oppositeProtocolFurnitures.length === 0 ? (
        <Typography variant='caption' fontWeight={700} display='block' mb={4}>
          Brak mebli do wyświetlenia
        </Typography>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: '100%' }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Typ</TableCell>
                <TableCell align='right'>Model</TableCell>
                <TableCell align='right'>Productent</TableCell>
                {!isReadOnly && <TableCell align='right'></TableCell>}
              </TableRow>
            </TableHead>
            {currentProtocol?.protocolType === PROTOCOL_TYPE.acceptance ? (
              <TableBody>
                {protocolFurnitures.map((furniture) => (
                  <TableRow
                    key={furniture.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component='th' scope='row'>
                      {furniture.furnitureType}
                    </TableCell>
                    <TableCell align='right'> {furniture.model}</TableCell>
                    <TableCell align='right'> {furniture.producer}</TableCell>
                    {!isReadOnly && (
                      <TableCell align='right'>
                        <IconButton
                          disabled={furnitureCreateform.isSubmitting}
                          onClick={() => removeProtocolFurniture(furniture.id)}
                        >
                          <DeleteForever />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                <>
                  {oppositeProtocolFurnitures.map((furniture) => (
                    <TableRow
                      key={furniture.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component='th' scope='row'>
                        {furniture.furnitureType}
                      </TableCell>
                      <TableCell align='right'> {furniture.model}</TableCell>
                      <TableCell align='right'> {furniture.producer}</TableCell>
                      {!isReadOnly && (
                        <TableCell align='right'>
                          <IconButton
                            disabled={furnitureCreateform.isSubmitting}
                            onClick={() => removeProtocolFurniture(furniture.id)}
                          ></IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {protocolFurnitures.map((furniture) => (
                    <TableRow
                      key={furniture.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component='th' scope='row'>
                        {furniture.furnitureType}
                      </TableCell>
                      <TableCell align='right'>{furniture.model}</TableCell>
                      <TableCell align='right'>{furniture.producer}</TableCell>
                      {!isReadOnly && (
                        <TableCell align='right'>
                          <IconButton
                            disabled={furnitureCreateform.isSubmitting}
                            onClick={() => removeProtocolFurniture(furniture.id)}
                          >
                            <DeleteForever />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      )}

      {!isReadOnly && (
        <>
          <Typography variant='caption' fontWeight={700}>
            Dodaj meble
          </Typography>
          <form onSubmit={furnitureCreateform.handleSubmit}>
            <FormControl fullWidth sx={{ my: 2 }} error={!!furnitureCreateform.errors.furniture}>
              <InputLabel id='furniture'>Mebel</InputLabel>
              <Select
                disabled={furnitureCreateform.isSubmitting}
                labelId='furniture'
                value={furnitureCreateform.values.furniture}
                label='furniture'
                onChange={({ target }) =>
                  furnitureCreateform.setFieldValue('furniture', target.value)
                }
              >
                {furnitureList.map((furniture) => (
                  <MenuItem
                    key={furniture.key}
                    value={furniture.value}
                    disabled={protocolFurnitures.some((pF) => pF.furniture === furniture.value)}
                  >
                    {furniture.label}
                  </MenuItem>
                ))}
              </Select>
              {!!furnitureCreateform.errors.furniture && (
                <FormHelperText>{String(furnitureCreateform.errors.furniture)}</FormHelperText>
              )}
            </FormControl>
            {!!furnitureCreateform.values.furniture && (
              <FurnitureTypePreview
                id={furnitureCreateform.values.furniture}
                protocolId={Number(currentProtocol?.id)}
              />
            )}
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ mt: 2 }}
            >
              <AutoSaveIndicator
                isDirty={furnitureCreateform.dirty && !furnitureCreateform.hasUnsavedChanges}
                isAutoSaving={false}
                lastSavedTime={furnitureCreateform.lastSaved}
              />
              <Button type='submit' variant='contained' loading={furnitureCreateform.isSubmitting}>
                Dodaj
              </Button>
            </Stack>
          </form>
        </>
      )}

      <style>{AutoSaveStyles}</style>
    </Box>
  )
}

export default ProtocolFurnitures
